import type { AppFacilities } from "@glide/common-core/dist/js/components/types";
import {
    BrowserImageValidationSession,
    type ValidationSessionState,
} from "@glide/common-core/dist/js/components/image-validation-session";
import { ImageValidationWorkerBridge } from "@glide/common-core/dist/js/components/image-validation-worker";

/** Explicit operator entry; creating a controller does not activate validation. */
export function createImageValidationController(
    appID: string,
    facilities: Pick<AppFacilities, "locationSettings" | "callAuthCloudFunction">,
    workers: ServiceWorkerContainer,
    changed: (state: ValidationSessionState) => void
): { readonly start: () => Promise<boolean>; readonly stop: () => void } {
    const endpoint = `${facilities.locationSettings.containerURLPrefix}/preservedImage`;
    const session = new BrowserImageValidationSession(
        appID,
        endpoint,
        async (name, body) => {
            const response = await facilities.callAuthCloudFunction(name, body);
            // A 429 stops this attended test. No automatic retries or account expansion.
            if (response === undefined || !response.ok) throw new Error("Validation request failed");
            return await response.json();
        },
        state => {
            if (state !== "active") changed(state);
        }
    );
    const bridge = new ImageValidationWorkerBridge(appID, endpoint, session, workers);
    return {
        start: async () => {
            const active = await bridge.start();
            if (active) changed("active");
            return active;
        },
        stop: () => bridge.stop(),
    };
}
